import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { CtaSection } from '../components/CtaSection';
import { SectionHeading } from '../components/SectionHeading';
import { ProcessSection } from '../components/home/ProcessSection';
import { BilingualSection } from '../components/home/BilingualSection';
import { TestimonialsSection } from '../components/home/TestimonialsSection';
import { features } from '../data/features';
import { heroImage } from '../data/gallery';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeo } from '../hooks/useSeo';
const stats = [{
  value: '10+',
  label: {
    en: 'Years in business',
    es: 'Años de experiencia'
  }
}, {
  value: '500+',
  label: {
    en: 'Projects completed',
    es: 'Proyectos completados'
  }
}, {
  value: '2',
  label: {
    en: 'Languages on every job',
    es: 'Idiomas en cada trabajo'
  }
}, {
  value: '100%',
  label: {
    en: 'Licensed & insured work',
    es: 'Trabajo licenciado y asegurado'
  }
}];
export function About() {
  const {
    t,
    lang
  } = useLanguage();
  useSeo({
    title: lang === 'en' ? 'About M&S Multiservices | Family-Owned Dallas Contractor' : 'Sobre M&S Multiservices | Contratista Familiar en Dallas',
    description: lang === 'en' ? 'M&S Multiservices is a family-owned, licensed and insured painting, drywall and remodeling contractor with 10+ years of experience serving Dallas-Fort Worth in English and Spanish.' : 'M&S Multiservices es un contratista familiar de pintura, drywall y remodelación, con licencia, seguro y más de 10 años sirviendo a Dallas-Fort Worth en inglés y español.'
  });
  return <>
      <PageHeader eyebrow={t({
      en: 'About us',
      es: 'Sobre nosotros'
    })} title={t({
      en: 'A family business that treats your home like ours',
      es: 'Un negocio familiar que trata tu casa como la nuestra'
    })} description={t({
      en: 'M&S Multiservices started with two brothers, one truck and a rule we still keep: never leave a job you would not be proud to show your own family.',
      es: 'M&S Multiservices comenzó con dos hermanos, una camioneta y una regla que seguimos cumpliendo: nunca dejar un trabajo que no estarías orgulloso de mostrar a tu familia.'
    })} data-id="element-368" data-mp="2i7mgnhhjybuj" />

      <section className="bg-white" aria-labelledby="story-heading" data-id="element-369" data-mp="22mt6nj1gfxmm">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-24" data-id="element-370" data-mp="27lnp2jvifk67">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-16" data-id="element-371" data-mp="27ngvby27o2do">
            <div data-id="element-372" data-mp="2cmbo9stw77bi">
              <SectionHeading id="story-heading" eyebrow={t({
              en: 'Our story',
              es: 'Nuestra historia'
            })} title={t({
              en: 'Ten years of Dallas homes, one standard of work',
              es: 'Diez años de casas en Dallas, un solo estándar de trabajo'
            })} data-id="element-373" data-mp="2glsu4nd8e6h7" />
              <div className="mt-6 space-y-4 text-lg leading-relaxed text-ink/75" data-id="element-374" data-mp="r7e9of98x5ri">
                <p data-id="element-375" data-mp="jo51uqge1f4k">
                  {t({
                  en: 'We began taking small painting jobs across Oak Cliff and Garland more than a decade ago. Word travelled, the crew grew, and today we handle full interiors, drywall rebuilds, flooring and complete kitchen and bathroom remodels across the Metroplex — residential and commercial.',
                  es: 'Empezamos tomando trabajos pequeños de pintura en Oak Cliff y Garland hace más de una década. La recomendación de boca en boca creció, y hoy hacemos interiores completos, reconstrucción de drywall, pisos y remodelaciones completas de cocina y baño en todo el Metroplex — residencial y comercial.'
                })}
                </p>
                <p data-id="element-376" data-mp="olws7g1dwt6n">
                  {t({
                  en: 'What has not changed: the owners are still on the job site. When you get a quote from us, the person who measured your walls is the person answering your phone call three weeks later.',
                  es: 'Lo que no ha cambiado: los dueños siguen en la obra. Cuando recibes un presupuesto nuestro, la persona que midió tus paredes es la misma que contesta tu llamada tres semanas después.'
                })}
                </p>
                <p data-id="element-377" data-mp="r7mfmfa8lumv">
                  {t({
                  en: 'We are licensed, insured, and we put the warranty in writing. If something is not right, we come back. That is the whole business model.',
                  es: 'Estamos licenciados, asegurados, y ponemos la garantía por escrito. Si algo no está bien, regresamos. Ese es todo nuestro modelo de negocio.'
                })}
                </p>
              </div>
            </div>

            <div data-id="element-378" data-mp="1euuikqk70rlq">
              <img src={heroImage} alt={t({
              en: 'M&S Multiservices crew member painting a Dallas home interior',
              es: 'Miembro del equipo de M&S Multiservices pintando el interior de una casa en Dallas'
            })} className="aspect-[4/3] w-full rounded-xl object-cover shadow-card" loading="lazy" data-id="element-379" data-mp="1eucghdaunj5w" />
              <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-navy/10 bg-navy/10" data-id="element-380" data-mp="3n2hchngnyxru">
                {stats.map(stat => <div key={stat.value} className="bg-white p-6" data-id="element-381" data-mp="vj08hm35e5q">
                    <dt className="sr-only" data-id="element-382" data-mp="ty26i0kbd34">{t(stat.label)}</dt>
                    <dd data-id="element-383" data-mp="39c4098wqowwp">
                      <span className="block font-display text-3xl text-navy" data-id="element-384" data-mp="3999hrmxadm5x">{stat.value}</span>
                      <span className="mt-1 block text-sm text-ink/70" data-id="element-385" data-mp="34aery10wixcb">{t(stat.label)}</span>
                    </dd>
                  </div>)}
              </dl>
            </div>
          </div>

          <div className="mt-20" data-id="element-386" data-mp="3e8ujg53uc8mc">
            <SectionHeading eyebrow={t({
            en: 'How we work',
            es: 'Cómo trabajamos'
          })} title={t({
            en: 'The commitments behind every quote',
            es: 'Los compromisos detrás de cada presupuesto'
          })} data-id="element-387" data-mp="2qkb79eho7boz" />
            <ul className="mt-10 grid gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3" data-id="element-388" data-mp="3fbimalbtf2w7">
              {features.map(feature => <li key={feature.title.en} className="border-t border-navy/10 pt-6" data-id="element-389" data-mp="3k3s6dkq918qk">
                  <feature.icon className="h-7 w-7 text-gold-dark" aria-hidden="true" data-id="element-390" data-mp="3p1ts6df27m5q" />
                  <h3 className="mt-4 font-display text-xl text-navy" data-id="element-391" data-mp="2sbt1u9z1kxmb">{t(feature.title)}</h3>
                  <p className="mt-2 leading-relaxed text-ink/75" data-id="element-392" data-mp="2wcczrfrfw59i">{t(feature.description)}</p>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <BilingualSection data-id="element-393" data-mp="36ast7pdxvqm6" />
      <ProcessSection data-id="element-394" data-mp="36bc8szg4a2x1" />
      <TestimonialsSection data-id="element-395" data-mp="36cndd1hdzep7" />
      <CtaSection data-id="element-396" data-mp="368ptueqwcl4s" />
    </>;
}